import React from "react";
import { IntlProvider } from "react-intl";
import { Route, Switch, withRouter } from "react-router-dom";
import { Grid } from "semantic-ui-react";

import GuardedRoute from "./GuardedRoute";
import TopNavbarComputer from "./components/top-navbar/TopNavbarComputer";
import TopNavbarMobile from "./components/top-navbar/TopNavbarMobile";
import UserRole from "./data/enums/UserRole";
import AdminPage from "./pages/AdminPage";
import ChartPage from "./pages/ChartPage";
import CreateParishPage from "./pages/CreateParishPage";
import CreatePriestPage from "./pages/CreatePriestPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import NewPasswordPage from "./pages/NewPasswordPage";
import UnderConstruction from "./pages/NotFoundPage";
import ParishProfilePage from "./pages/ParishProfilePage";
import PriestProfilePage from "./pages/PriestProfilePage";
import RegisterPage from "./pages/RegisterPage";
import SearchParishesPage from "./pages/SearchParishesPage";
import SearchPriestsPage from "./pages/SearchPriestsPage";
import SearchSuggestionsPage from "./pages/SearchSuggestionsPage";
import allLanguagesConfig from "./translation/allLanguagesConfig";
import useLanguage from "./translation/useLanguage";
import useSession from "./util/useSession/useSession";

const AppRouter: React.FC = () => {
    const { languageConfig, handleLanguageChange } = useLanguage();
    const { user, setUser, handleLogout } = useSession();

    return (
        <IntlProvider key={languageConfig.locale} locale={languageConfig.locale} messages={languageConfig.messages}>
            <main>
                <Grid className="ui computer only computerMenu">
                    <TopNavbarComputer
                        allLanguagesConfig={allLanguagesConfig}
                        currentURL={window.location.href}
                        handleLanguageChange={handleLanguageChange}
                        handleLogout={handleLogout}
                        language={languageConfig.key}
                        user={user}
                    />
                </Grid>

                <Grid className="ui mobile tablet only">
                    <TopNavbarMobile
                        allLanguagesConfig={allLanguagesConfig}
                        currentURL={window.location.href}
                        handleLanguageChange={handleLanguageChange}
                        handleLogout={handleLogout}
                        language={languageConfig.key}
                        user={user}
                    />
                </Grid>

                <Switch>
                    <Route exact path="/" render={() => <HomePage user={user} />} />
                    <Route path="/login" render={() => <LoginPage setUser={setUser} user={user} />} />
                    <Route component={RegisterPage} path="/register" />
                    <Route component={ForgotPasswordPage} path="/forgotPassword" />
                    <Route component={NewPasswordPage} path="/newPassword" />
                    <GuardedRoute
                        path="/administration"
                        render={() => <AdminPage selfID={user ? user.ID : undefined} />}
                        roles={[UserRole.ADMIN, UserRole.OWNER]}
                        user={user}
                    />

                    <GuardedRoute
                        component={SearchPriestsPage}
                        path="/priests/search"
                        roles={[UserRole.GUEST, UserRole.READER, UserRole.EDITOR, UserRole.ADMIN, UserRole.OWNER]}
                        user={user}
                    />
                    <GuardedRoute
                        component={SearchParishesPage}
                        path="/parishes/search"
                        roles={[UserRole.GUEST, UserRole.READER, UserRole.EDITOR, UserRole.ADMIN, UserRole.OWNER]}
                        user={user}
                    />
                    <GuardedRoute
                        component={SearchSuggestionsPage}
                        path="/suggestions/search"
                        roles={[UserRole.EDITOR, UserRole.ADMIN, UserRole.OWNER]}
                        user={user}
                    />

                    <GuardedRoute
                        path="/priests/profile/:id"
                        render={({ match }) => <PriestProfilePage priestID={match.params.id} userRole={user ? user.role : undefined} />}
                        roles={[UserRole.READER, UserRole.EDITOR, UserRole.ADMIN, UserRole.OWNER]}
                        user={user}
                    />
                    <GuardedRoute
                        path="/parishes/profile/:id"
                        render={({ match }) => <ParishProfilePage parishID={match.params.id} userRole={user ? user.role : undefined} />}
                        roles={[UserRole.GUEST, UserRole.READER, UserRole.EDITOR, UserRole.ADMIN, UserRole.OWNER]}
                        user={user}
                    />

                    <GuardedRoute component={CreatePriestPage} path="/priests/create" roles={[UserRole.EDITOR, UserRole.ADMIN]} user={user} />
                    <GuardedRoute
                        component={CreateParishPage}
                        path="/parishes/create"
                        roles={[UserRole.EDITOR, UserRole.ADMIN, UserRole.OWNER]}
                        user={user}
                    />

                    <GuardedRoute
                        component={ChartPage}
                        path="/charts"
                        roles={[UserRole.GUEST, UserRole.READER, UserRole.EDITOR, UserRole.ADMIN, UserRole.OWNER]}
                        user={user}
                    />

                    <Route component={UnderConstruction} />
                </Switch>
            </main>
        </IntlProvider>
    );
};

export default withRouter(AppRouter);
